import React from "react";
import { Container } from "react-bootstrap";
import { Link } from "react-router-dom";

import architectureDiagram from "../../Assets/Projects/cyber-range/enterprise-cyber-range-architecture.jpg";
import "./CyberRange.css";

const vlans = [
  { id: 10, name: "Infrastructure", subnet: "10.10.10.0/24", systems: "DC01 · TS-ROUTER01" },
  { id: 20, name: "Servers", subnet: "10.10.20.0/24", systems: "APP01 · Application services" },
  { id: 30, name: "Users", subnet: "10.10.30.0/24", systems: "WIN11-01 · Workstations" },
  { id: 40, name: "Security", subnet: "10.10.40.0/24", systems: "SEC01 · Wazuh / SIEM" },
  { id: 50, name: "Voice", subnet: "10.10.50.0/24", systems: "IP phones · Voice gateways" },
  { id: 60, name: "Guest", subnet: "10.10.60.0/24", systems: "Guest devices · Internet only" },
  { id: 70, name: "IoT", subnet: "10.10.70.0/24", systems: "Sensors · IoT devices" }
];

function CyberRange() {
  return (
    <section className="range-section" id="cyber-range">
      <Container>
        <header className="range-header">
          <span>Flagship environment</span>
          <h2>
            Stephen Enterprise <strong>Cyber Range</strong>
          </h2>
          <p>
            A self-built enterprise environment where I design networks, administer
            Windows and Linux systems, deploy business applications, monitor security
            events, and investigate realistic activity.
          </p>
        </header>

        <div className="range-intro">
          <div className="range-copy">
            <h3>One environment connecting infrastructure and defensive security</h3>
            <p>
              Proxmox hosts the virtual environment, while OPNsense provides routing,
              NAT, firewall enforcement, and VLAN gateways. Active Directory, Odoo,
              Windows and Linux systems, Wazuh monitoring, and secure remote access all
              operate within the same segmented architecture.
            </p>

            <dl className="range-facts">
              <div>
                <dt>7</dt>
                <dd>segmented networks</dd>
              </div>
              <div>
                <dt>OPNsense</dt>
                <dd>central routing and policy control</dd>
              </div>
              <div>
                <dt>Tailscale</dt>
                <dd>encrypted remote administration</dd>
              </div>
            </dl>

            <div className="range-actions">
              <Link to="/projects/enterprise-network">Explore the implementation</Link>
              <a href={architectureDiagram} target="_blank" rel="noopener noreferrer">
                Open full diagram
              </a>
            </div>
          </div>

          <figure className="range-diagram">
            <a href={architectureDiagram} target="_blank" rel="noopener noreferrer">
              <img
                src={architectureDiagram}
                alt="Enterprise cyber range architecture with Proxmox, OPNsense, seven VLANs and Tailscale remote access"
              />
            </a>
            <figcaption>
              Proxmox virtualization, OPNsense network control, seven VLANs, enterprise
              services, and secure remote administration through Tailscale.
            </figcaption>
          </figure>
        </div>

        <section className="range-remote" aria-labelledby="remote-access-title">
          <div>
            <span>Secure remote access</span>
            <h3 id="remote-access-title">Administering the lab without exposing it publicly</h3>
            <p>
              An Ubuntu-based Tailscale subnet router advertises approved internal
              subnets to authorized devices. This gives me encrypted access over the
              internet while Proxmox, OPNsense, Active Directory, Wazuh, and internal
              applications remain off the public internet.
            </p>
          </div>

          <ol className="remote-flow">
            <li><strong>Admin device</strong><span>Authorized laptop</span></li>
            <li><strong>Tailscale</strong><span>WireGuard-based overlay</span></li>
            <li><strong>TS-ROUTER01</strong><span>Advertised internal routes</span></li>
            <li><strong>Lab systems</strong><span>Access permitted by policy</span></li>
          </ol>
        </section>

        <section className="range-vlans" aria-labelledby="vlan-title">
          <div className="range-subheading">
            <span>Network design</span>
            <h3 id="vlan-title">Seven-VLAN architecture</h3>
          </div>

          <div className="vlan-table-wrap">
            <table className="vlan-table">
              <thead>
                <tr>
                  <th>VLAN</th>
                  <th>Purpose</th>
                  <th>Subnet</th>
                  <th>Current systems</th>
                </tr>
              </thead>
              <tbody>
                {vlans.map((vlan) => (
                  <tr key={vlan.id}>
                    <td>{vlan.id}</td>
                    <td>{vlan.name}</td>
                    <td>{vlan.subnet}</td>
                    <td>{vlan.systems}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </Container>
    </section>
  );
}

export default CyberRange;
